import { TFunction } from 'i18next';

import uiHelpers from '../../../helpers/ui.helper';
import { SMAInterval } from '../../../reducers/preference.reducer';
import { ConstituentSMAsItem } from '../../../shared/types';
import { PriceDataPoint } from '../../../types';
import {
  ExternalSeriesGenerator,
  getDefaultToolptipFormatter,
  PriceViewerSeries,
} from './base';
import { fillPriceDates } from './utils';

const lineColors: Record<SMAInterval, string> = {
  50: 'red',
  150: 'green',
  200: 'orange',
};

/**
 * ConstituentSMA can be used for both IndexSMA and MarketSMA, and it is controlled by the first argument "option"
 */
class ConstituentSMA extends ExternalSeriesGenerator {
  yAxisFormatter = uiHelpers.formatPercentage;
  isSeparateFromPriceChart = true;
  height = 120;
  activeWindows: SMAInterval[];

  constituentSMAs: readonly ConstituentSMAsItem[];
  isMarketSMA: boolean;
  indexName?: string;

  constructor(
    constituentSMAs: readonly ConstituentSMAsItem[],
    option: 'market' | 'constituent',
    activeWindows: SMAInterval[],
    t: TFunction
  ) {
    super(t);
    this.constituentSMAs = constituentSMAs;
    this.activeWindows = activeWindows;
    this.isMarketSMA = option === 'market';
  }

  getChartName(): string {
    let updateTime,
      updateTimeStr = '';
    if (this.constituentSMAs.length > 0) {
      updateTime = [...this.constituentSMAs]
        .reverse()
        .find(data => data?.updateTime)?.updateTime;
    }
    if (updateTime !== undefined && updateTime !== null && updateTime !== 0) {
      updateTime = uiHelpers.formatDateTimeLocalFromTimestamp(updateTime);
      updateTimeStr = ' ' + this.t('Updated at') + ' ' + updateTime;
    }
    return (
      (this.isMarketSMA ? this.t('MarketSMA') : this.t('IndexSMA')) +
      updateTimeStr
    );
  }

  getSeries(dataPoints: ReadonlyArray<PriceDataPoint>): PriceViewerSeries[] {
    const priceDates = dataPoints.map(item => item.date.toString());
    const dates = this.constituentSMAs.map(({ date }) => date);
    return this.activeWindows.map(window => {
      const values = this.constituentSMAs.map(point => {
        return (
          point[`sma${window}_smaller_price`] /
          (point[`sma${window}_larger_price`] +
            point[`sma${window}_smaller_price`])
        );
      });
      return {
        name:
          (this.isMarketSMA ? this.t('MarketSMA') : this.t('IndexSMA')) +
          '-' +
          window,
        echartSeries: {
          type: 'line',
          data: fillPriceDates(priceDates, values, dates),
          symbol: 'none',
          lineStyle: {
            color: lineColors[String(window)],
          },
        },
        tooltipFormatter: getDefaultToolptipFormatter('2d', 'percentage', {
          keyColor: lineColors[String(window)],
        }),
      };
    });
  }
}

export default ConstituentSMA;
